import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { Users, ScanLine, TrendingUp, Sparkles, Activity, Inbox } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";

function KpiCard({ icon: Icon, label, value, accent = "#4CAF50", suffix = "" }) {
  return (
    <div className="glass-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] uppercase tracking-wider text-muted-soft">{label}</span>
        <span className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: `${accent}28`, boxShadow: `0 0 14px ${accent}40` }}>
          <Icon size={16} style={{ color: accent }} />
        </span>
      </div>
      <p className="text-2xl font-extrabold" style={{ color: accent, textShadow: `0 0 12px ${accent}40` }}>
        {suffix}{typeof value === "number" ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

const TOOLTIP_STYLE = {
  background: "rgba(10,10,10,0.92)",
  border: "1px solid rgba(76,175,80,0.35)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 12,
};

export default function DashboardScreen() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get("/dashboard");
        setData(data);
      } catch {}
    })();
  }, []);

  if (!data) {
    return (
      <AdminLayout>
        <div className="p-12 text-center">
          <span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" />
        </div>
      </AdminLayout>
    );
  }

  const k = data.kpis;

  return (
    <AdminLayout>
      <div className="mb-7">
        <h1 className="text-2xl sm:text-3xl font-bold text-shadow-green">Dashboard</h1>
        <p className="text-muted-soft text-sm mt-1">Real-time view across users, scans, and revenue.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <KpiCard icon={Users} label="Total Users" value={k.total_users} />
        <KpiCard icon={Sparkles} label="MRR" value={k.mrr.toFixed(2)} suffix="$" accent="#FFD54F" />
        <KpiCard icon={ScanLine} label="Total Scans" value={k.total_scans} accent="#7B1FA2" />
        <KpiCard icon={Inbox} label="Pending Content" value={k.pending_content} accent="#FF8F00" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="glass-card p-5">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">New users — last 7 days</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.signups_7d}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Line type="monotone" dataKey="value" stroke="#4CAF50" strokeWidth={2.4} dot={{ r: 3, fill: "#4CAF50" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="glass-card p-5">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Scans — last 7 days</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.scans_7d}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} allowDecimals={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="value" fill="#7B1FA2" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="glass-card p-5 lg:col-span-1">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Tier breakdown</p>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data.tier_breakdown} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.tier_breakdown.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 space-y-1.5">
            {data.tier_breakdown.map((t) => (
              <div key={t.name} className="flex items-center gap-2 text-xs">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: t.color }} />
                <span className="flex-1 text-soft">{t.name}</span>
                <span className="font-semibold">{t.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="glass-card p-5 lg:col-span-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Activity summary</p>
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: "Identifications", value: k.identify_scans, icon: ScanLine, color: "#4CAF50" },
              { label: "Diagnoses", value: k.diagnose_scans, icon: Activity, color: "#FF8F00" },
              { label: "Tracked Plants", value: k.journals, icon: TrendingUp, color: "#7B1FA2" },
              { label: "Journal Entries", value: k.journal_entries, icon: TrendingUp, color: "#FFD54F" },
            ].map((m, idx) => (
              <div key={idx} className="rounded-lg p-3 flex items-center gap-3"
                   style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)" }}>
                <span className="w-9 h-9 rounded-full flex items-center justify-center"
                      style={{ background: `${m.color}25` }}>
                  <m.icon size={16} style={{ color: m.color }} />
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-soft">{m.label}</p>
                  <p className="text-base font-bold">{m.value.toLocaleString()}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 p-3 rounded-lg" style={{ background: "rgba(76,175,80,0.06)", border: "1px solid rgba(76,175,80,0.18)" }}>
            <p className="text-[11px] uppercase tracking-wider text-muted-soft">Annualised Revenue (estimated)</p>
            <p className="text-xl font-bold text-[#4CAF50] mt-0.5">${k.arr.toLocaleString()}</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
